/**
 * Domivo: любими продукти.
 *
 * Държи се изцяло в localStorage - без app, без акаунт, без разходи.
 * Компромисът е съзнателен: списъкът живее в браузъра, не се пренася между
 * устройства. Ако някога потрябва синхронизация, същият store се подменя с
 * customer metafield, а трите елемента отдолу остават непокътнати.
 */
(() => {
  const KEY = 'domivo:wishlist';
  const EVENT = 'domivo:wishlist:change';

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  const write = (handles) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch {
      /* частен режим / пълна квота - тихо се отказваме, UI-ът остава коректен */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles } }));
  };

  const store = {
    all: read,
    has: (handle) => read().includes(handle),
    toggle(handle) {
      const handles = read();
      const at = handles.indexOf(handle);
      if (at === -1) handles.unshift(handle);
      else handles.splice(at, 1);
      write(handles);
      return at === -1;
    },
    remove(handle) {
      write(read().filter((h) => h !== handle));
    },
  };

  window.DomivoWishlist = store;

  // Друг таб е пипнал списъка - синхронизираме без презареждане.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles: read() } }));
  });

  class WishButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('product-handle');
      this.button = this.querySelector('button');
      if (!this.handle || !this.button) return;

      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        store.toggle(this.handle);
      });
      document.addEventListener(EVENT, this.sync);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.sync);
    }

    sync() {
      const on = store.has(this.handle);
      this.button.setAttribute('aria-pressed', String(on));
      this.button.setAttribute(
        'aria-label',
        on ? 'Премахни от любими' : 'Добави в любими'
      );
      this.toggleAttribute('data-active', on);
    }
  }

  class WishCount extends HTMLElement {
    connectedCallback() {
      this.sync = this.sync.bind(this);
      document.addEventListener(EVENT, this.sync);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.sync);
    }

    sync() {
      const n = store.all().length;
      this.textContent = n > 0 ? String(n) : '';
      this.toggleAttribute('hidden', n === 0);
    }
  }

  class WishList extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener(EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.render);
    }

    async render() {
      const handles = store.all();
      const empty = this.querySelector('[data-wish-empty]');
      const grid = this.querySelector('[data-wish-grid]');
      if (!grid || !empty) return;

      if (handles.length === 0) {
        grid.innerHTML = '';
        empty.hidden = false;
        return;
      }
      empty.hidden = true;

      // Продукт, който вече не съществува, се изхвърля от списъка тихо -
      // иначе празна карта би останала завинаги.
      const results = await Promise.all(
        handles.map((handle) =>
          fetch(`/products/${encodeURIComponent(handle)}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );

      const alive = [];
      const cards = [];
      results.forEach((product, i) => {
        if (!product) return;
        alive.push(handles[i]);
        cards.push(this.card(product, handles[i]));
      });

      if (alive.length !== handles.length) write(alive);
      grid.innerHTML = cards.join('');

      grid.querySelectorAll('[data-wish-remove]').forEach((btn) => {
        btn.addEventListener('click', () => store.remove(btn.getAttribute('data-wish-remove')));
      });
      // „Добави в количката" на бързата карта - същото като в категориите
      if (!grid.dmvAddBound && window.dmvCards) {
        grid.dmvAddBound = true;
        grid.addEventListener('click', (e) => {
          const btn = e.target.closest('button.dmv-lcard__btn[data-add]');
          if (btn) { e.preventDefault(); window.dmvCards.quickAdd(btn); }
        });
      }
    }

    /* Същата карта като в категориите и търсенето (domivo-glass.js/lightCard):
       снимка, сърце (тук премахва от любими), марка, цена, наличност, количка. */
    card(product, handle) {
      if (window.dmvCards) {
        const src = product.featured_image || '';
        const image = src.replace(/^(https?:)?\/\/[^/]+\/(cdn\/shop\/|s\/files\/\d+\/\d+\/\d+\/\d+\/)?/, '').split('?')[0];
        const avail = (product.variants || []).find((v) => v.available) || (product.variants || [])[0] || {};
        const cap = product.compare_at_price && product.compare_at_price > product.price ? product.compare_at_price / 100 : null;
        return window.dmvCards.lightCard({
          id: product.id, handle, title: product.title, url: product.url, vendor: product.vendor,
          image, price: product.price / 100, compare_at: cap, available: product.available,
          variant_id: avail.id, variants: (product.variants || []).length,
        });
      }
      return this.legacyCard(product, handle);
    }

    legacyCard(product, handle) {
      const money = (cents) =>
        new Intl.NumberFormat('bg-BG', {
          style: 'currency',
          currency: this.getAttribute('currency') || 'BGN',
        }).format(cents / 100);

      const img = product.featured_image
        ? `<img src="${product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2')}" alt="" loading="lazy" width="400" height="400">`
        : '';

      const price = product.price_varies
        ? `от ${money(product.price_min)}`
        : money(product.price);

      const soldOut = product.available
        ? ''
        : '<span class="dmv-wish__oos">Изчерпан</span>';

      return `
        <article class="dmv-wish__card">
          <a class="dmv-wish__media" href="${product.url}">${img}</a>
          <div class="dmv-wish__body">
            <a class="dmv-wish__title" href="${product.url}">${product.title}</a>
            <p class="dmv-wish__price">${price} ${soldOut}</p>
          </div>
          <button class="dmv-wish__remove" type="button" data-wish-remove="${handle}" aria-label="Премахни ${product.title}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
          </button>
        </article>`;
    }
  }

  if (!customElements.get('domivo-wish-button')) customElements.define('domivo-wish-button', WishButton);
  if (!customElements.get('domivo-wish-count')) customElements.define('domivo-wish-count', WishCount);
  if (!customElements.get('domivo-wish-list')) customElements.define('domivo-wish-list', WishList);
})();
